import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { getEntityInstanceListNew, upsertEntityInstance } from "./entity-instance.service";
import { entitySpecByRole } from "./feature-templates";
import type {
  ContributionSpec,
  EntitySpec,
  FeatureSpec,
  FieldSpec,
  RelationSpec,
  RelationTarget,
} from "./feature-templates";

export interface ActualEntity {
  id: string;
  name: string;
  entity_fields?: { data: Array<{ name: string }> };
  entity_relations?: {
    data: Array<{
      field_name: string;
      type: string;
      target_field_name?: string;
      target_entity: { data: { id: string } };
    }>;
  };
}

/**
 * role -> entity id. Always empty today, so roles fall back to their default
 * name. The indirection is the seam: supplying bindings later lets a tenant
 * rename a feature entity, or point a role at an entity it already had, without
 * a single caller changing.
 */
export type RoleBindings = Record<string, string>;

export interface FeaturePlan {
  createEntities: Array<EntitySpec>;
  addFields: Array<{
    role: string;
    entityId: string;
    entityName: string;
    fields: Array<FieldSpec>;
  }>;
  addRelations: Array<{
    role: string;
    entityId: string;
    entityName: string;
    relations: Array<RelationSpec & { target_entity_id: string }>;
  }>;
  /** Contributions whose host entity does not exist yet — blocked on a dependency. */
  pendingContributions: Array<ContributionSpec>;
}

export const resolveRole = <T extends { id: string; name: string }>(
  spec: EntitySpec,
  entities: Array<T>,
  bindings?: RoleBindings,
): T | undefined => {
  const boundId = bindings?.[spec.role];
  return boundId
    ? entities.find((entity) => entity.id === boundId)
    : entities.find((entity) => entity.name === spec.defaultName);
};

const resolveRoleId = (
  role: string,
  entities: Array<ActualEntity>,
  bindings?: RoleBindings,
): string | undefined => {
  const spec = entitySpecByRole.get(role);
  return spec ? resolveRole(spec, entities, bindings)?.id : undefined;
};

const resolveTargetId = (
  target: RelationTarget,
  entities: Array<ActualEntity>,
  bindings?: RoleBindings,
): string | undefined =>
  "entityId" in target ? target.entityId : resolveRoleId(target.role, entities, bindings);

const resolveHost = (
  role: string,
  entities: Array<ActualEntity>,
  bindings?: RoleBindings,
): ActualEntity | undefined => {
  const spec = entitySpecByRole.get(role);
  return spec ? resolveRole(spec, entities, bindings) : undefined;
};

const hasRelation = (
  actual: ActualEntity,
  relation: RelationSpec,
  targetEntityId: string,
): boolean =>
  (actual.entity_relations?.data ?? []).some(
    (existing) =>
      existing.field_name === relation.field_name &&
      existing.type === relation.type &&
      existing.target_entity.data.id === targetEntityId &&
      (existing.target_field_name ?? "") === relation.target_field_name,
  );

/**
 * Diff desired against actual. Everything downstream — the feature switches, the
 * gate, the "missing fields" card and `applyFeature` — reads this one function,
 * so a feature can never be checked against a different shape than it is created
 * with.
 */
export const planFeature = (
  feature: FeatureSpec,
  entities: Array<ActualEntity>,
  bindings?: RoleBindings,
): FeaturePlan => {
  const plan: FeaturePlan = {
    createEntities: [],
    addFields: [],
    addRelations: [],
    pendingContributions: [],
  };

  for (const spec of feature.entities) {
    const actual = resolveRole(spec, entities, bindings);

    if (!actual) {
      plan.createEntities.push(spec);
      continue;
    }

    const existingFieldNames = (actual.entity_fields?.data ?? []).map((field) => field.name);
    const missingFields = (spec.fields ?? []).filter(
      (field) => !existingFieldNames.includes(field.name),
    );
    if (missingFields.length > 0) {
      plan.addFields.push({
        role: spec.role,
        entityId: actual.id,
        entityName: actual.name,
        fields: missingFields,
      });
    }

    const missingRelations = (spec.relations ?? []).flatMap((relation) => {
      const targetEntityId = resolveTargetId(relation.target, entities, bindings);
      // Target not resolvable yet means it is still queued for creation; the
      // relation gets added once it exists.
      if (!targetEntityId) return [];
      return hasRelation(actual, relation, targetEntityId)
        ? []
        : [{ ...relation, target_entity_id: targetEntityId }];
    });
    if (missingRelations.length > 0) {
      plan.addRelations.push({
        role: spec.role,
        entityId: actual.id,
        entityName: actual.name,
        relations: missingRelations,
      });
    }
  }

  for (const contribution of feature.contributes ?? []) {
    const host = resolveHost(contribution.role, entities, bindings);
    const targetEntityId = resolveTargetId(contribution.relation.target, entities, bindings);

    if (!host || !targetEntityId) {
      plan.pendingContributions.push(contribution);
      continue;
    }
    if (hasRelation(host, contribution.relation, targetEntityId)) continue;

    plan.addRelations.push({
      role: contribution.role,
      entityId: host.id,
      entityName: host.name,
      relations: [{ ...contribution.relation, target_entity_id: targetEntityId }],
    });
  }

  return plan;
};

export const isPlanEmpty = (plan: FeaturePlan): boolean =>
  plan.createEntities.length === 0 &&
  plan.addFields.length === 0 &&
  plan.addRelations.length === 0 &&
  plan.pendingContributions.length === 0;

const createEntityMutation = gql`
  mutation ReconcilerCreateEntity($input: CreateEntityInput!) {
    createEntity(input: $input) {
      data {
        id
        name
      }
    }
  }
`;

const updateEntityMutation = gql`
  mutation ReconcilerUpdateEntity($input: UpdateEntityInput!) {
    updateEntity(input: $input) {
      data {
        id
      }
    }
  }
`;

export const addFieldsToEntity = async (
  entityId: string,
  fields: Array<{ name: string; data_type: string }>,
) => {
  if (fields.length === 0) return;
  await createGraphQLClient().request(updateEntityMutation, {
    input: { id: entityId, entity_fields_add: fields },
  });
};

export const addRelationsToEntity = async (
  entityId: string,
  relations: Array<{
    field_name: string;
    type: string;
    target_entity_id: string;
    target_field_name?: string;
  }>,
) => {
  for (const relation of relations) {
    await createGraphQLClient().request(updateEntityMutation, {
      input: {
        id: entityId,
        entity_relations_add: [
          {
            field_name: relation.field_name,
            type: relation.type,
            target_entity: { id: relation.target_entity_id },
            target_field_name: relation.target_field_name ?? "",
          },
        ],
      },
    });
  }
};

const findRecordId = async (
  entityName: string,
  matchOn: string,
  value: unknown,
): Promise<string | undefined> => {
  const { entityInstances } = await getEntityInstanceListNew<Record<string, unknown>>({
    entityName,
    paginationInput: { page: 1, per_page: 100 },
    fieldsToFetch: ["id", matchOn],
  });
  return entityInstances.find((instance) => instance[matchOn] === value)?.id as string | undefined;
};

const seedRecords = async (feature: FeatureSpec, nameByRole: Map<string, string>) => {
  for (const recordSpec of feature.records ?? []) {
    const entityName = nameByRole.get(recordSpec.role);
    if (!entityName) continue;

    const { entityInstances } = await getEntityInstanceListNew<Record<string, unknown>>({
      entityName,
      paginationInput: { page: 1, per_page: 1 },
      fieldsToFetch: ["id"],
    });
    if (entityInstances.length > 0) continue;

    for (const row of recordSpec.rows) {
      const data: Record<string, unknown> = { ...(row.values ?? {}) };
      for (const [relationField, ref] of Object.entries(row.connect ?? {})) {
        const targetName = nameByRole.get(ref.role);
        const targetId = targetName
          ? await findRecordId(targetName, ref.matchOn, ref.value)
          : undefined;
        if (targetId) {
          // to-one relation input generated by the backend for `<field>`
          data[`${relationField}_set`] = { id: targetId };
        }
      }

      // Generic per-entity mutation built from the resolved name — no feature
      // code hardcodes a generated operation, so a renamed entity still works.
      await upsertEntityInstance({ entityName, data, dontTriggerFlows: true });
    }
  }
};

/**
 * Converge a feature to its declared shape. Idempotent, and safe from any
 * partial state: whatever already exists is kept and only the gap is filled.
 */
export const applyFeature = async (
  feature: FeatureSpec,
  entities: Array<ActualEntity>,
  bindings?: RoleBindings,
) => {
  const idByRole = new Map<string, string>();
  const nameByRole = new Map<string, string>();

  // Roles are global, so seed from every role this feature touches — owned,
  // hosted by another feature, or referenced by a seed row.
  const referencedRoles = new Set<string>([
    ...feature.entities.map((spec) => spec.role),
    ...(feature.contributes ?? []).map((contribution) => contribution.role),
    ...(feature.records ?? []).map((record) => record.role),
  ]);
  for (const role of referencedRoles) {
    const spec = entitySpecByRole.get(role);
    const actual = spec ? resolveRole(spec, entities, bindings) : undefined;
    if (actual) {
      idByRole.set(role, actual.id);
      nameByRole.set(role, actual.name);
    }
  }

  // Entities first, relations after, so a relation between two new entities has
  // both ends available and creation order stops mattering.
  for (const spec of feature.entities) {
    if (idByRole.has(spec.role)) continue;
    const result = (await createGraphQLClient().request(createEntityMutation, {
      input: {
        name: spec.defaultName,
        entity_fields_add: spec.fields ?? [],
      },
    })) as { createEntity: { data: { id: string; name: string } } };
    idByRole.set(spec.role, result.createEntity.data.id);
    nameByRole.set(spec.role, result.createEntity.data.name);
  }

  for (const spec of feature.entities) {
    const entityId = idByRole.get(spec.role);
    if (!entityId) continue;

    const actual = resolveRole(spec, entities, bindings);

    if (actual) {
      const existingFieldNames = (actual.entity_fields?.data ?? []).map((field) => field.name);
      await addFieldsToEntity(
        entityId,
        (spec.fields ?? []).filter((field) => !existingFieldNames.includes(field.name)),
      );
    }

    const relationsToAdd = (spec.relations ?? []).flatMap((relation) => {
      const targetEntityId =
        "entityId" in relation.target
          ? relation.target.entityId
          : idByRole.get(relation.target.role);
      if (!targetEntityId) return [];
      if (actual && hasRelation(actual, relation, targetEntityId)) return [];
      return [{ ...relation, target_entity_id: targetEntityId }];
    });
    await addRelationsToEntity(entityId, relationsToAdd);
  }

  for (const contribution of feature.contributes ?? []) {
    const hostId = idByRole.get(contribution.role);
    const targetEntityId =
      "entityId" in contribution.relation.target
        ? contribution.relation.target.entityId
        : idByRole.get(contribution.relation.target.role);
    if (!hostId || !targetEntityId) continue;

    const host = resolveHost(contribution.role, entities, bindings);
    if (host && hasRelation(host, contribution.relation, targetEntityId)) continue;

    await addRelationsToEntity(hostId, [
      { ...contribution.relation, target_entity_id: targetEntityId },
    ]);
  }

  await seedRecords(feature, nameByRole);
};
